import { ERROR_CODES, type ErrorCode } from '@diario/common';
import { type Type, type } from 'arktype';
import { env } from './env';

/**
 * Error de la API con código traducible (clave `errores.ERR-NNN` del namespace i18n `common`) y,
 * si el servidor lo mandó, el `details` crudo del cuerpo de error (`{ code, details? }`, ver
 * `DomainErrorFilter` del backend). Es lo único que permite a una pantalla leer el motivo de un
 * 403 (p. ej. `details.motivo` de `POST /experiences`, documento 03a §6.2 — CL-03b.2 del documento
 * 03b) en vez de mostrar siempre el texto genérico del código. `details` nunca se muestra en
 * crudo: quien lo consume lo traduce con sus propias claves literales (p. ej.
 * `experiences:motivos.*`), nunca interpolándolo directamente en la interfaz.
 *
 * `status` y `retryAfterMs` (corrección de rate limiting) llevan el código HTTP de la respuesta y,
 * si el servidor cortó por límite de peticiones, cuánto queda de espera según la cabecera
 * `Retry-After` (en milisegundos, ya convertida desde los segundos que manda esa cabecera). Ambos
 * van al FINAL y son opcionales para no romper a quien ya construye `new ApiError(code)` o
 * `new ApiError(code, details)` (`useExperiences.ts`, `auth.stories.tsx`).
 */
export class ApiError extends Error {
	constructor(
		readonly code: ErrorCode | string,
		readonly details?: unknown,
		readonly status?: number,
		readonly retryAfterMs?: number,
	) {
		super(code);
		this.name = 'ApiError';
	}
}

/** Primer código HTTP de la familia 4xx (error del cliente). */
const PRIMER_STATUS_4XX = 400;
/** Primer código HTTP que ya no es 4xx (arranca la familia 5xx). */
const PRIMER_STATUS_5XX = 500;

/**
 * `true` para un error que reintentar no arregla: un 4xx significa que la petición en sí está mal
 * formada, no autorizada, prohibida, apunta a un recurso que no existe, o que el propio servidor
 * pidió esperar (429) — insistir de inmediato no cambia el resultado. Solo mira `status`: sin él
 * (p. ej. un fallo de red, que esta clase representa sin `status`), no se puede saber si reintentar
 * ayudaría, así que no se considera no-reintentable.
 */
export const esErrorNoReintentable = (error: unknown): boolean =>
	error instanceof ApiError
	&& error.status !== undefined
	&& error.status >= PRIMER_STATUS_4XX
	&& error.status < PRIMER_STATUS_5XX;

/** Cabecera de espera de un cubo de límite de peticiones SIN nombre (`Retry-After` estándar, en
 * segundos). Tras esta corrección, es la que corta en las rutas de dominio: los cubos CON nombre
 * (`porCorreo`, `porIp`) son opt-in y solo se aplican en `POST /auth/recover`. */
const CABECERA_RETRY_AFTER = 'Retry-After';

/** Milisegundos por segundo, para convertir el valor (en segundos) de `Retry-After`. */
const MS_POR_SEGUNDO = 1000;

/**
 * Lee `Retry-After` de una respuesta y la convierte a milisegundos. `@nestjs/throttler` manda esta
 * cabecera SIN sufijo solo para el throttler global (sin nombre); un cubo CON nombre la manda con
 * sufijo (`Retry-After-porIp`, `Retry-After-porCorreo`) — con los cubos con nombre vueltos opt-in
 * (solo `POST /auth/recover` los pide), la estándar es la que de verdad importa aquí, así que es
 * la única que se lee. Devuelve `undefined` si la cabecera no vino, no es un número, o la propia
 * respuesta no expone `headers` — decenas de dobles de test en todo el repositorio construyen su
 * `Response` falso a mano con solo `{ json, ok, status }` (p. ej. `tests/useSesion.test.tsx`), sin
 * `headers`; usar `respuesta.headers.get(...)` a pelo reventaría esos dobles con un `TypeError`
 * ajeno al contrato que se está probando.
 */
const leerRetryAfterMs = (respuesta: Response): number | undefined => {
	const segundos = respuesta.headers?.get(CABECERA_RETRY_AFTER);
	if (segundos === null || segundos === undefined) {
		return undefined;
	}
	const valor = Number(segundos);
	return Number.isFinite(valor) ? valor * MS_POR_SEGUNDO : undefined;
};

/**
 * Cliente HTTP contra el backend. Es la ÚNICA vía de datos del frontend: nunca se llama a
 * Supabase desde el navegador (regla del proyecto y ADR-0005).
 *
 * Toda respuesta se valida con el contrato de `@diario/common`, así que una API que se desvía
 * del contrato falla aquí, con un error tipado, en vez de propagar datos inesperados por la UI.
 */
export const apiGet = async <T extends Type>(ruta: string, schema: T): Promise<T['infer']> => {
	let respuesta: Response;
	try {
		respuesta = await fetch(`${env.VITE_API_URL}${ruta}`, {
			credentials: 'same-origin',
			headers: { Accept: 'application/json' },
		});
	} catch {
		// Fallo de red: el servidor no respondió.
		throw new ApiError(ERROR_CODES.internal);
	}

	if (!respuesta.ok) {
		const cuerpo = (await respuesta.json().catch(() => null)) as { code?: string; details?: unknown } | null;
		throw new ApiError(
			cuerpo?.code ?? ERROR_CODES.internal,
			cuerpo?.details,
			respuesta.status,
			leerRetryAfterMs(respuesta),
		);
	}

	const validado = schema(await respuesta.json());
	if (validado instanceof type.errors) {
		throw new ApiError(ERROR_CODES.validation);
	}
	return validado as T['infer'];
};

/** Lanza el `ApiError` correspondiente a una respuesta HTTP no satisfactoria (mutaciones). */
const lanzarErrorRespuesta = async (respuesta: Response): Promise<never> => {
	const cuerpo = (await respuesta.json().catch(() => null)) as { code?: string; details?: unknown } | null;
	throw new ApiError(
		cuerpo?.code ?? ERROR_CODES.internal,
		cuerpo?.details,
		respuesta.status,
		leerRetryAfterMs(respuesta),
	);
};

/** Lee el cuerpo de una respuesta como JSON, tolerando una respuesta sin cuerpo (p. ej. 204). */
const leerCuerpoOpcional = async (respuesta: Response): Promise<unknown> => {
	const texto = await respuesta.text();
	return texto.length > 0 ? JSON.parse(texto) : undefined;
};

/** Valida el cuerpo recibido contra el esquema ArkType; lanza `ApiError(validation)` si no cumple. */
const validarCuerpo = <T extends Type>(schema: T, datos: unknown): T['infer'] => {
	const validado = schema(datos);
	if (validado instanceof type.errors) {
		throw new ApiError(ERROR_CODES.validation);
	}
	return validado as T['infer'];
};

/**
 * Envía una mutación (`POST` / `PATCH` / `DELETE`) al backend. Mismo tratamiento de fallo de red
 * que `apiGet`; el cuerpo, si lo hay, viaja como JSON con su `Content-Type`.
 */
const enviarMutacion = async (
	metodo: 'POST' | 'PATCH' | 'DELETE',
	ruta: string,
	cuerpo?: unknown,
): Promise<Response> => {
	try {
		return await fetch(`${env.VITE_API_URL}${ruta}`, {
			body: cuerpo === undefined ? undefined : JSON.stringify(cuerpo),
			credentials: 'same-origin',
			headers:
				cuerpo === undefined
					? { Accept: 'application/json' }
					: { Accept: 'application/json', 'Content-Type': 'application/json' },
			method: metodo,
		});
	} catch {
		// Fallo de red: el servidor no respondió.
		throw new ApiError(ERROR_CODES.internal);
	}
};

/** `POST` contra el backend: envía `cuerpo` como JSON y valida la respuesta contra `schema`. */
export const apiPost = async <T extends Type>(ruta: string, schema: T, cuerpo?: unknown): Promise<T['infer']> => {
	const respuesta = await enviarMutacion('POST', ruta, cuerpo);
	if (!respuesta.ok) {
		return lanzarErrorRespuesta(respuesta);
	}
	return validarCuerpo(schema, await leerCuerpoOpcional(respuesta));
};

/** `PATCH` contra el backend: envía `cuerpo` como JSON y valida la respuesta contra `schema`. */
export const apiPatch = async <T extends Type>(ruta: string, schema: T, cuerpo?: unknown): Promise<T['infer']> => {
	const respuesta = await enviarMutacion('PATCH', ruta, cuerpo);
	if (!respuesta.ok) {
		return lanzarErrorRespuesta(respuesta);
	}
	return validarCuerpo(schema, await leerCuerpoOpcional(respuesta));
};

/**
 * `DELETE` contra el backend. Envía `cuerpo` como JSON cuando se proporciona (p. ej. el `motivo`
 * de `cancelJoinSchema`) y valida la respuesta contra `schema`, tolerando que el backend responda
 * sin cuerpo (p. ej. `204 No Content`).
 */
export const apiDelete = async <T extends Type>(ruta: string, schema: T, cuerpo?: unknown): Promise<T['infer']> => {
	const respuesta = await enviarMutacion('DELETE', ruta, cuerpo);
	if (!respuesta.ok) {
		return lanzarErrorRespuesta(respuesta);
	}
	return validarCuerpo(schema, await leerCuerpoOpcional(respuesta));
};
